'use client';

const SUCCESS_CALLBACK_NAME = 'onKakaobankAffiliateAuthSuccess';
const UPDATE_CANCEL_CALLBACK_NAME = 'onKakaobankUpdateCanceled';
const DOCS_COMPLETE_CALLBACK_NAME = 'onKakaobankDocsCompleted';

//* 규격이 전역 콜백 이름 하나만 쓰게 해 시도가 동시에 살 수 없다 — 새 시도가 이전 시도를 걷는다
let supersedePendingAuth: (() => void) | undefined;

interface KakaobankInterface {
  activateTransaction: (compositeKey: string, successCallback: string) => void;
  close: (closeCode: string) => void;
  showUpdatePopup: (cancelCallback: string) => void;
  openDocs: (
    title: string,
    url: string,
    buttonBackgroundColorHex: string,
    buttonTextColorHex: string,
    completeCallback: string,
  ) => void;
  openApp: (url: string) => void;
}

declare global {
  interface Window {
    KabangB2bJavascriptInterface?: KakaobankInterface;
    webkit?: {
      messageHandlers?: {
        b2bBridge?: { postMessage: (message: string) => void };
      };
    };
    onKakaobankAffiliateAuthSuccess?: (compositeKey?: string) => void;
    //* (Android Only) 카뱅 앱이 뒤로가기에서 호출한다 — 우리가 심어야 하는 쪽이다
    onBackPressed?: () => void;
    onKakaobankUpdateCanceled?: () => void;
    onKakaobankDocsCompleted?: () => void;
  }
}

const readNativeChannel = () =>
  typeof window === 'undefined'
    ? undefined
    : window.webkit?.messageHandlers?.b2bBridge;

const postToNative = (payload: unknown) => {
  const channel = readNativeChannel();

  if (!channel) {
    throw new Error('카카오뱅크 네이티브 채널을 찾지 못했습니다.');
  }

  channel.postMessage(JSON.stringify(payload));
};

//* Android는 네이티브가 같은 이름으로 먼저 심어 두므로 덮지 않는다
export const installKakaobankInterface = () => {
  if (typeof window === 'undefined' || window.KabangB2bJavascriptInterface) {
    return;
  }

  if (!readNativeChannel()) {
    return;
  }

  window.KabangB2bJavascriptInterface = {
    activateTransaction: (compositeKey, successCallback) =>
      postToNative({
        msg_type: 'activate_transaction',
        msg_content: {
          composite_key: compositeKey,
          success_callback: successCallback,
        },
      }),
    close: (closeCode) =>
      postToNative({ msg_type: 'close', msg_content: closeCode }),
    //* 규격이 cancelCallback을 JSON 문자열로 받아 여기서 parse한다 — 객체를 그대로 넘기면 어긋난다
    showUpdatePopup: (cancelCallback) =>
      postToNative({
        msg_type: 'show_update_popup',
        msg_content: JSON.parse(cancelCallback),
      }),
    openDocs: (
      title,
      url,
      buttonBackgroundColorHex,
      buttonTextColorHex,
      completeCallback,
    ) =>
      postToNative({
        msg_type: 'open_docs',
        msg_content: {
          title,
          url,
          button_bg_color_hex: buttonBackgroundColorHex,
          button_text_color_hex: buttonTextColorHex,
          complete_callback: completeCallback,
        },
      }),
    openApp: (url) => postToNative({ msg_type: 'open_app', msg_content: url }),
  };
};

export const hasKakaobankBridge = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    typeof readNativeChannel()?.postMessage === 'function' ||
    typeof window.KabangB2bJavascriptInterface?.activateTransaction ===
      'function'
  );
};

//* 카뱅 앱이 구버전이면 함수가 없다 — 그때 NOT_FOUND_FUNCTION이 온다
export const toBridgeErrorCode = (error: unknown) => {
  const code = (error as { code?: unknown } | undefined)?.code;

  return typeof code === 'string' ? code : 'UNKNOWN';
};

const readTransactionKey = (data: unknown) => {
  if (
    typeof data !== 'object' ||
    data === null ||
    !('transaction_key' in data)
  ) {
    return undefined;
  }

  const { transaction_key: transactionKey } = data;

  return typeof transactionKey === 'string' && transactionKey
    ? transactionKey
    : undefined;
};

//* 완료 신호가 문서마다 달라(v1.0=success_callback · v2.9=postMessage) 양쪽을 다 받는다
export const requestAffiliateAuth = (compositeKey: string) =>
  new Promise<void>((resolve, reject) => {
    installKakaobankInterface();

    const nativeInterface = window.KabangB2bJavascriptInterface;

    if (!nativeInterface?.activateTransaction) {
      reject(new Error('카카오뱅크 앱브리지를 찾지 못했습니다.'));

      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (readTransactionKey(event.data)) {
        settle();
      }
    };

    const teardown = () => {
      window.removeEventListener('message', handleMessage);
      Reflect.deleteProperty(window, SUCCESS_CALLBACK_NAME);
      supersedePendingAuth = undefined;
    };

    const settle = () => {
      teardown();
      resolve();
    };

    supersedePendingAuth?.();
    supersedePendingAuth = () => {
      teardown();
      reject(Object.assign(new Error('SUPERSEDED'), { code: 'SUPERSEDED' }));
    };

    window.addEventListener('message', handleMessage);
    //* 규격 §2.2.2 — 같은 콜백이 취소에도 불리고 성공일 때만 compositeKey가 실려 온다
    window.onKakaobankAffiliateAuthSuccess = (returnedKey) => {
      if (returnedKey) {
        settle();

        return;
      }

      teardown();
      reject(Object.assign(new Error('USER_CANCEL'), { code: 'USER_CANCEL' }));
    };

    try {
      //* Android WebView의 @JavascriptInterface는 주입된 객체 자신에서 불러야 한다 —
      //* 메서드를 변수로 뽑아 호출하면 non-injected object로 판정돼 거부된다
      nativeInterface.activateTransaction(compositeKey, SUCCESS_CALLBACK_NAME);
    } catch (error) {
      teardown();
      reject(error);
    }
  });

//* Android WebView의 @JavascriptInterface는 주입된 객체 자신을 수신자로 불러야 한다 —
//* 메서드를 변수로 뽑으면 non-injected object로 판정돼 거부된다
const readNativeInterface = (method: keyof KakaobankInterface) => {
  installKakaobankInterface();

  const nativeInterface = window.KabangB2bJavascriptInterface;

  if (!nativeInterface?.[method]) {
    throw new Error('카카오뱅크 앱브리지를 찾지 못했습니다.');
  }

  return nativeInterface;
};

//* 전역 이름을 문자열로 넘기는 규격이라 부른 뒤엔 우리가 거둬야 다음 호출과 겹치지 않는다
const registerOneShotCallback = (
  name: 'onKakaobankUpdateCanceled' | 'onKakaobankDocsCompleted',
  handler: () => void,
) => {
  window[name] = () => {
    Reflect.deleteProperty(window, name);
    handler();
  };
};

export const requestKakaobankClose = () => {
  readNativeInterface('close').close('00');
};

//* 이 팝업을 부르는 시점은 이미 구버전 앱을 만난 뒤다 — 팝업 자체가 없는 것도 정상 경로라 던지지 않는다
export const requestKakaobankUpdatePopup = (onCancel: () => void) => {
  installKakaobankInterface();

  const nativeInterface = window.KabangB2bJavascriptInterface;

  if (!nativeInterface?.showUpdatePopup) {
    return false;
  }

  registerOneShotCallback(UPDATE_CANCEL_CALLBACK_NAME, onCancel);

  nativeInterface.showUpdatePopup(
    JSON.stringify({ cancelCallback: `${UPDATE_CANCEL_CALLBACK_NAME}()` }),
  );

  return true;
};

interface KakaobankDocs {
  title: string;
  url: string;
  buttonBackgroundColor: string;
  buttonTextColor: string;
  onComplete?: () => void;
}

export const openKakaobankDocs = ({
  title,
  url,
  buttonBackgroundColor,
  buttonTextColor,
  onComplete,
}: KakaobankDocs) => {
  const nativeInterface = readNativeInterface('openDocs');

  if (onComplete) {
    registerOneShotCallback(DOCS_COMPLETE_CALLBACK_NAME, onComplete);
  }

  nativeInterface.openDocs(
    title,
    url,
    buttonBackgroundColor,
    buttonTextColor,
    onComplete ? `${DOCS_COMPLETE_CALLBACK_NAME}()` : '',
  );
};
